import axios from "axios";
import { message } from "antd";
import useAuthStore from "../../stores/useAuthStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import messages from "../../errors/messages.en-IN.json";
import { getLocalNetworkPermission, POPUP_KINDS, dismissPrintFailure } from "../printer/printFailureNotice";

/**
 * CW Hardware Controller watchdog (Web mode only — CW Browser talks to its own shell).
 *
 * Support tickets come from three things the cashier cannot see: the Controller is not running,
 * Chrome is blocking the page from reaching it (Local Network Access), or the printer itself is off.
 * This module keeps one status for the till and a small queue of bills that did not reach the
 * Controller, so the header chip (HardwareStatusChip) can say what is wrong and what to do.
 *
 *   status  notConfigured  no Controller address on this till (chip hidden)
 *           checking       first check, or a single miss not yet confirmed
 *           ok             the Controller answered (and, with /status, the receipt printer is ready)
 *           degraded       the Controller answered but the receipt printer is not ready (detail names it)
 *           down           no answer twice in a row
 *           blocked        Chrome's local network permission is "prompt" or "denied"
 *           outdated       the Controller is older than posConfig.hcMinVersion
 *
 * Checks: GET {imageUrl}status (new Controllers). A Controller that answers /status with anything
 * other than a status reply is an older one: it is remembered and checked with GET {imageUrl}printer,
 * where any HTTP answer means it is running. While the permission is "prompt" no request is made
 * until the cashier clicks Check again (Chrome asks for that permission after a click only).
 *
 * Off switch: posConfig.hcWatchdog = "N".
 */

export const POLL_MS = 15000;
export const PROBE_TIMEOUT_MS = 3000;
export const RECHECK_AFTER_MISS_MS = 3000;
export const MISSES_FOR_DOWN = 2;
export const PRINT_TIMEOUT_MS = 10000;
export const DRAWER_TIMEOUT_MS = 5000;
export const POLE_TIMEOUT_MS = 3000;
export const QUEUE_KEY = "cw.hcPrintQueue";
export const QUEUE_MAX = 30;
export const QUEUE_AUTO_RETRY_MAX_AGE_MS = 2 * 60 * 60 * 1000;
export const RETRY_GAP_MS = 1000;
// A duplicate KOT means the kitchen cooks twice; Close Till / day end show their own popup.
export const NEVER_QUEUE_KINDS = new Set(["kot", "cancelKot", ...POPUP_KINDS]);

const hwText = () => messages?.hardware || {};
const fill = (text, values) => Object.entries(values).reduce((t, [k, v]) => t.split(`{${k}}`).join(String(v)), text || "");

// ── Configuration ───────────────────────────────────────────────────────────────

const isElectron = () => typeof window !== "undefined" && !!window.electronAPI;

export const getControllerUrl = () => {
  const url = useAuthStore.getState()?.tillData?.tillAccess?.cwrTill?.hardwareController?.imageUrl;
  return typeof url === "string" && /^https?:\/\//i.test(url) ? url : null;
};

// Installer link for CW Hardware Controller, from env REACT_APP_HC_DOWNLOAD_URL (set per deployment).
// Only http(s) links are used; anything else (or unset) hides the Download button.
export const getControllerDownloadUrl = (raw = process.env.REACT_APP_HC_DOWNLOAD_URL) => {
  const url = typeof raw === "string" ? raw.trim() : "";
  return /^https?:\/\/\S+$/i.test(url) ? url : null;
};

// Default ON in Web mode when the till has a Controller address; posConfig hcWatchdog "N" turns it off.
export const isWatchdogEnabled = () => {
  if (isElectron()) return false;
  if (!getControllerUrl()) return false;
  return usePOSConfigStore.getState()?.posConfig?.hcWatchdog !== "N";
};

const withSlash = (url) => (url.endsWith("/") ? url : `${url}/`);

// ── Store ───────────────────────────────────────────────────────────────────────

const listeners = new Set();
let snapshot = {
  status: "notConfigured",
  detail: "",
  source: null, // "status" (new Controller) | "printer" (older Controller)
  version: null,
  checkedAt: 0,
  misses: 0,
  queue: [],
};

const emit = (patch) => {
  snapshot = { ...snapshot, ...patch };
  listeners.forEach((l) => l());
};
export const subscribeHardwareStatus = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getHardwareStatus = () => snapshot;

// ── /status reply ───────────────────────────────────────────────────────────────

const versionParts = (v) =>
  String(v || "")
    .split(".")
    .map((p) => parseInt(p, 10) || 0);
export const isOlderVersion = (version, minimum) => {
  if (!version || !minimum) return false;
  const a = versionParts(version);
  const b = versionParts(minimum);
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    if ((a[i] || 0) !== (b[i] || 0)) return (a[i] || 0) < (b[i] || 0);
  }
  return false;
};

const isStatusReply = (data) => !!data && typeof data === "object" && Array.isArray(data.printers);

// Reads the Controller's /status reply: the receipt printer decides ok / degraded.
export const readStatusReply = (data) => {
  const chip = hwText().chip || {};
  const printers = data.printers || [];
  const receipt = printers.find((p) => p?.role === "receipt") || printers[0];
  const minimum = usePOSConfigStore.getState()?.posConfig?.hcMinVersion;
  let result = { status: "ok", detail: "" };
  if (!receipt || receipt.connected === false) result = { status: "degraded", detail: chip.printer_off || "Printer not connected" };
  else if (receipt.paper === "out") result = { status: "degraded", detail: chip.paper_out || "Printer: out of paper" };
  else if (receipt.coverOpen === true) result = { status: "degraded", detail: chip.lid_open || "Printer: lid open" };
  else if (receipt.paper === "low") result = { status: "degraded", detail: chip.paper_low || "Printer: low paper" };
  if (result.status === "ok" && isOlderVersion(data.version, minimum)) result = { status: "outdated", detail: "" };
  return { ...result, version: data.version || null };
};

// ── Checks ──────────────────────────────────────────────────────────────────────

const olderControllers = new Set(); // Controller addresses with no /status
let inFlight = null;
let recheckTimer = null;
let wasDownOrBlocked = false;

const probe = async (base) => {
  const cfg = { timeout: PROBE_TIMEOUT_MS, hcProbe: true };
  if (!olderControllers.has(base)) {
    try {
      const res = await axios.get(`${base}status`, cfg);
      if (isStatusReply(res?.data)) return { answered: true, source: "status", ...readStatusReply(res.data) };
      olderControllers.add(base);
      return { answered: true, source: "printer", status: "ok", detail: "" };
    } catch (error) {
      if (!error?.response) return { answered: false, error };
      // It answered, just not with a status reply: an older Controller.
      olderControllers.add(base);
      return { answered: true, source: "printer", status: "ok", detail: "" };
    }
  }
  try {
    await axios.get(`${base}printer`, cfg);
    return { answered: true, source: "printer", status: "ok", detail: "" };
  } catch (error) {
    if (error?.response) return { answered: true, source: "printer", status: "ok", detail: "" };
    return { answered: false, error };
  }
};

const settle = (patch) => {
  const next = { ...patch, checkedAt: Date.now() };
  const recovered = next.status === "ok" && wasDownOrBlocked;
  if (next.status === "down" || next.status === "blocked") wasDownOrBlocked = true;
  else if (next.status === "ok" || next.status === "degraded" || next.status === "outdated") wasDownOrBlocked = false;
  emit(next);
  if (recovered) retryAllQueued({ auto: true });
};

/**
 * One check now. manual: the cashier clicked Check again — the request is made even while the
 * browser permission is "prompt", so Chrome can show its Allow box.
 */
export const checkHardwareNow = ({ manual = false } = {}) => {
  if (!isWatchdogEnabled()) {
    if (snapshot.status !== "notConfigured") emit({ status: "notConfigured", detail: "", misses: 0 });
    return Promise.resolve(snapshot);
  }
  if (inFlight) return inFlight;
  const base = withSlash(getControllerUrl());
  inFlight = (async () => {
    if (manual || snapshot.status === "notConfigured") emit({ status: "checking", misses: 0 });
    const permission = await getLocalNetworkPermission(base);
    if (!manual && (permission === "prompt" || permission === "denied")) {
      settle({ status: "blocked", detail: "", misses: 0 });
      return snapshot;
    }
    const result = await probe(base);
    if (result.answered) {
      clearTimeout(recheckTimer);
      settle({ status: result.status, detail: result.detail, source: result.source, version: result.version || null, misses: 0 });
      return snapshot;
    }
    if (permission === "denied") {
      settle({ status: "blocked", detail: "", misses: 0 });
      return snapshot;
    }
    const misses = snapshot.misses + 1;
    if (misses >= MISSES_FOR_DOWN) {
      settle({ status: "down", detail: "", misses });
    } else {
      // One slow reply must not flip the chip: check again soon before calling it down.
      emit({ misses, status: snapshot.status === "ok" || snapshot.status === "degraded" ? snapshot.status : "checking" });
      clearTimeout(recheckTimer);
      recheckTimer = setTimeout(() => checkHardwareNow(), RECHECK_AFTER_MISS_MS);
    }
    return snapshot;
  })().finally(() => {
    inFlight = null;
  });
  return inFlight;
};

// ── Polling (started by the chip; only while the POS tab is in front) ────────────

let pollers = 0;
let pollTimer = null;
const isVisible = () => typeof document === "undefined" || document.visibilityState !== "hidden";
const onVisibility = () => {
  if (isVisible()) checkHardwareNow();
};

export const startHardwareWatch = () => {
  pollers += 1;
  if (pollers === 1) {
    checkHardwareNow();
    pollTimer = setInterval(() => {
      if (isVisible()) checkHardwareNow();
    }, POLL_MS);
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility);
  }
  return () => {
    pollers = Math.max(0, pollers - 1);
    if (pollers === 0) {
      clearInterval(pollTimer);
      clearTimeout(recheckTimer);
      pollTimer = null;
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
    }
  };
};

// Billing paths that need the Controller (card machine) ask before sending.
export const isControllerUnreachable = () => isWatchdogEnabled() && (snapshot.status === "down" || snapshot.status === "blocked");

// ── Print queue ─────────────────────────────────────────────────────────────────

const readQueue = () => {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};
const writeQueue = (queue) => {
  try {
    if (queue.length) localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    else localStorage.removeItem(QUEUE_KEY);
  } catch {
    // storage full or blocked: the queue still lives for this session
  }
  emit({ queue });
};
snapshot = { ...snapshot, queue: readQueue() };

const isTimeout = (error) => error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT" || /timeout/i.test(error?.message || "");
const headerValue = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") return headers.get(name) || undefined;
  const key = Object.keys(headers).find((k) => k.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : undefined;
};

/**
 * Should this failed print be kept and sent again later? Only when it surely never reached the
 * Controller: a network error with no answer. A timeout may already have printed (sending it again
 * prints twice), KOTs must never print twice, and Close Till / day end show their own popup.
 */
export const isQueueablePrintFailure = (config, error) => {
  if (!config || !error || error.response || isTimeout(error)) return false;
  if (config.hcQueueRetry) return false;
  if (NEVER_QUEUE_KINDS.has(config.printKind)) return false;
  if (!/^https?:\/\//i.test(config.url || "")) return false;
  return isWatchdogEnabled();
};

// Called by the print-service interceptor. Returns true when the print was queued.
export const enqueueFailedPrint = (config, error) => {
  if (!isQueueablePrintFailure(config, error)) return false;
  const entry = {
    id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    url: config.url,
    data: typeof config.data === "string" ? config.data : JSON.stringify(config.data ?? ""),
    contentType: headerValue(config.headers, "Content-Type") || "text/plain",
    kind: config.printKind || "print",
    ref: config.printRef || "",
    displayRef: config.printDisplayRef || "",
    createdAt: Date.now(),
    attempts: 0,
  };
  const queue = [...readQueue(), entry];
  writeQueue(queue.slice(-QUEUE_MAX));
  // A print just failed with no answer: the chip may still say ready, so check now.
  checkHardwareNow();
  return true;
};

export const removeQueuedPrint = (id) => writeQueue(readQueue().filter((e) => e.id !== id));

const sendQueued = (entry) =>
  axios.post(entry.url, entry.data, {
    headers: { "Content-Type": entry.contentType },
    timeout: PRINT_TIMEOUT_MS,
    hcQueueRetry: true,
  });

// Sends one waiting print. Resolves "printed" | "failed" | "unsure" (timed out: it may have printed).
export const printQueuedNow = async (id) => {
  const entry = readQueue().find((e) => e.id === id);
  if (!entry) return "printed";
  try {
    await sendQueued(entry);
    removeQueuedPrint(id);
    dismissPrintFailure(entry.kind, entry.ref);
    return "printed";
  } catch (error) {
    if (isTimeout(error)) {
      // It may have printed: never send it a third time on its own.
      removeQueuedPrint(id);
      return "unsure";
    }
    writeQueue(readQueue().map((e) => (e.id === id ? { ...e, attempts: (e.attempts || 0) + 1, lastError: error?.message || "failed" } : e)));
    return "failed";
  }
};

let retrying = false;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// auto: after the Controller came back — only prints under 2 hours old.
export const retryAllQueued = async ({ auto = false } = {}) => {
  if (retrying) return { printed: 0, failed: 0, unsure: 0 };
  retrying = true;
  const counts = { printed: 0, failed: 0, unsure: 0 };
  try {
    const now = Date.now();
    const due = readQueue().filter((e) => !auto || now - e.createdAt < QUEUE_AUTO_RETRY_MAX_AGE_MS);
    for (let i = 0; i < due.length; i += 1) {
      if (i > 0) await wait(RETRY_GAP_MS);
      const result = await printQueuedNow(due[i].id);
      counts[result] += 1;
      if (result === "failed") break; // still down: keep the rest for the next recovery
    }
  } finally {
    retrying = false;
  }
  const q = hwText().queue || {};
  try {
    if (counts.printed) message.success(fill(counts.printed === 1 ? q.printed_one : q.printed_many, { count: counts.printed }) || `Printed ${counts.printed} waiting bills.`);
    if (counts.unsure) message.warning(q.unsure || "A waiting bill may not have printed. Check the printer before printing it again.");
  } catch {
    // outside the React tree
  }
  return counts;
};

// ── Drawer / pole / other device failures ────────────────────────────────────────

const lastDeviceNotice = {};
const DEVICE_NOTICE_GAP_MS = { pole: 5 * 60 * 1000, drawer: 0 };

// Drawer and pole failures used to be silent. Shown once per device (pole: at most every 5 minutes,
// it fires on every cart change), and a no-answer failure triggers a status check.
export const reportDeviceFailure = (device, error) => {
  if (isElectron()) return;
  const now = Date.now();
  const gap = DEVICE_NOTICE_GAP_MS[device] ?? 60 * 1000;
  if (lastDeviceNotice[device] && now - lastDeviceNotice[device] < gap) return;
  lastDeviceNotice[device] = now;
  const d = (hwText().devices || {})[device] || {};
  const text = error?.response ? d.error : d.no_answer;
  try {
    message.warning({ content: text || `${device} did not respond.`, key: `hc-device-${device}`, duration: 6 });
  } catch {
    // outside the React tree
  }
  if (!error?.response) checkHardwareNow();
};

// Tests only.
export const __resetHardwareStatusForTests = () => {
  clearTimeout(recheckTimer);
  clearInterval(pollTimer);
  pollTimer = null;
  pollers = 0;
  inFlight = null;
  retrying = false;
  wasDownOrBlocked = false;
  olderControllers.clear();
  Object.keys(lastDeviceNotice).forEach((k) => delete lastDeviceNotice[k]);
  snapshot = { status: "notConfigured", detail: "", source: null, version: null, checkedAt: 0, misses: 0, queue: readQueue() };
};
